/**
 * AgentScopeGate — stops agent-scoped pages from rendering when no agent
 * is selected, showing a purpose-built state instead.
 *
 * WHY THIS EXISTS
 * ---------------
 * Switching to an organization that has NO agents correctly clears the
 * selected agent (AutoSelectClient calls select(null)). But the page the
 * user is standing on would keep showing the PREVIOUS agent's data, for
 * two reasons:
 *
 *   1. Agent pages key their queries on the agent id and gate them with
 *      `enabled: agentId !== ""`. With no agent the queries simply don't
 *      run — so nothing overwrites what's already on screen.
 *   2. Several of those queries use `placeholderData: (prev) => prev`,
 *      which deliberately keeps the last successful data visible while a
 *      new fetch is in flight. With no new fetch coming, that placeholder
 *      never goes away.
 *
 * Neither is a bug on its own — together they mean "no agent" looked
 * identical to "same agent as before". Rather than patching eight pages
 * (and every page added later), this gate makes the rule structural: if a
 * route needs an agent and there isn't one, the page doesn't render at all.
 *
 * It distinguishes THREE situations, because they need different words:
 *   • still resolving  → spinner (prevents an empty-state flash on org switch)
 *   • org has no agents → "No agents enrolled", with an enrol call-to-action
 *   • agents exist, none picked → "Select an agent", pointing at the top bar
 *
 * Mounted once in AppShell, wrapping the page content.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Spinner } from "@shared/ui";
import { useCan } from "@features/auth/hooks/useCan";
import { usePickerAgents, useSelectedClient } from "./hooks/useRegistry";
import { useEffectiveOrgId } from "./hooks/useOrgPicker";
import styles from "./AgentScopeGate.module.scss";

/**
 * Routes whose content is meaningless without a selected agent — every page
 * that reads `useSelectedClient()` and feeds the agent id into its queries.
 *
 * Deliberately an explicit list rather than something derived from NAV_ITEMS'
 * `scope` field: almost all of these are declared `scope: "org"` in nav.ts
 * (only FIM is `scope: "agent"`), so the nav model can't tell them apart.
 *
 * NOT included, on purpose — these are org-scoped and must still render with
 * no agent picked:
 *   /endpoint-inventory   the list you pick an agent FROM
 *   /org-dashboard, /organizations, /group-dashboard, /agent-enrollment,
 *   /user-management, /fim-management, /alert-thresholds, /event-volume
 *
 * Add a route here when you add a new page that requires an agent.
 */
const AGENT_SCOPED_ROUTES: ReadonlySet<string> = new Set([
  "/agent-dashboard",
  "/system-monitoring",
  "/cis-benchmark",
  "/compliance",
  "/vulnerability-detection",
  "/authentication-access",
  "/threat-hunting",
  "/file-integrity-monitoring",
]);

/**
 * Routes gated ONLY when the organization has no agents at all — never on
 * whether an agent is selected.
 *
 * The Security Client Registry is the list you PICK an agent from, so it must
 * keep rendering with nothing selected (gating it on selection would remove
 * the only way to choose one). But when the org has zero agents the page is
 * just scaffolding around the same fact repeated four times — three empty
 * cards and an empty table all saying "No agents enrolled yet". One clear
 * message plus an enrol action is more useful than four echoes of nothing.
 */
const ORG_EMPTY_ROUTES: ReadonlySet<string> = new Set([
  "/endpoint-inventory",
]);

export const AgentScopeGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { selected } = useSelectedClient();
  const orgId = useEffectiveOrgId();
  const { can } = useCan();

  // Same cache entry ClientSelector and AutoSelectClient already use, so this
  // costs no extra network request — it just lets us tell "this org has no
  // agents at all" apart from "agents exist, none selected yet".
  const list = usePickerAgents(orgId);

  const needsAgent = AGENT_SCOPED_ROUTES.has(pathname);
  const gateWhenOrgEmpty = ORG_EMPTY_ROUTES.has(pathname);
  const hasAgent = Boolean(selected?.agent_id);

  // Not a gated route at all → always render.
  if (!needsAgent && !gateWhenOrgEmpty) return <>{children}</>;

  // Agent-scoped route with an agent in hand → render.
  if (needsAgent && hasAgent) return <>{children}</>;

  // Still resolving which agent to use. Without this branch, switching org
  // briefly paints the empty state before AutoSelectClient picks the new
  // org's first agent — a visible flicker on every switch.
  if (!orgId || list.isLoading || (list.isFetching && !list.data)) {
    return (
      <div className={styles.loading}>
        <Spinner size={18} />
        <span>Loading agents…</span>
      </div>
    );
  }

  const orgHasNoAgents = (list.data?.length ?? 0) === 0;

  // Registry-style route: the org HAS agents, so the page is useful even
  // with nothing selected — that's where the user picks one. Only the
  // genuinely-empty org falls through to the message below.
  if (gateWhenOrgEmpty && !orgHasNoAgents) return <>{children}</>;

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <span className={`${styles.badge} ${orgHasNoAgents ? styles.badgeMuted : ""}`}>
          <i className={`fas ${orgHasNoAgents ? "fa-server" : "fa-hand-pointer"}`} />
        </span>

        {orgHasNoAgents ? (
          <>
            <h2 className={styles.title}>No agents enrolled</h2>
            <p className={styles.message}>
              This organization doesn&apos;t have any agents yet. Enroll an agent to start collecting security data for this page.
            </p>
            {can("agent:enroll") && (
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  size="md"
                  icon="fa-download"
                  onClick={() => navigate("/agent-enrollment")}
                >
                  Enroll an agent
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <h2 className={styles.title}>Select an agent to continue</h2>
            <p className={styles.message}>
              This page reports on a single host. Choose which agent you&apos;d like
              to view.
            </p>
            <span className={styles.hint}>
              <i className="fas fa-arrow-up" />
              Use the agent picker in the bar above
            </span>
          </>
        )}
      </div>
    </div>
  );
};


// /**
//  * AgentScopeGate — stops agent-scoped pages from rendering when no agent
//  * is selected, showing a purpose-built state instead.
//  *
//  * WHY THIS EXISTS
//  * ---------------
//  * Switching to an organization that has NO agents correctly clears the
//  * selected agent (AutoSelectClient calls select(null)). But the page the
//  * user is standing on would keep showing the PREVIOUS agent's data, for
//  * two reasons:
//  *
//  *   1. Agent pages key their queries on the agent id and gate them with
//  *      `enabled: agentId !== ""`. With no agent the queries simply don't
//  *      run — so nothing overwrites what's already on screen.
//  *   2. Several of those queries use `placeholderData: (prev) => prev`,
//  *      which deliberately keeps the last successful data visible while a
//  *      new fetch is in flight. With no new fetch coming, that placeholder
//  *      never goes away.
//  *
//  * Neither is a bug on its own — together they mean "no agent" looked
//  * identical to "same agent as before". Rather than patching eight pages
//  * (and every page added later), this gate makes the rule structural: if a
//  * route needs an agent and there isn't one, the page doesn't render at all.
//  *
//  * It distinguishes THREE situations, because they need different words:
//  *   • still resolving  → spinner (prevents an empty-state flash on org switch)
//  *   • org has no agents → "No agents enrolled", with an enrol call-to-action
//  *   • agents exist, none picked → "Select an agent", pointing at the top bar
//  *
//  * Mounted once in AppShell, wrapping the page content.
//  */
// import { useLocation, useNavigate } from "react-router-dom";
// import { Button, Spinner } from "@shared/ui";
// import { useCan } from "@features/auth/hooks/useCan";
// import { usePickerAgents, useSelectedClient } from "./hooks/useRegistry";
// import { useEffectiveOrgId } from "./hooks/useOrgPicker";
// import styles from "./AgentScopeGate.module.scss";

// /**
//  * Routes whose content is meaningless without a selected agent — every page
//  * that reads `useSelectedClient()` and feeds the agent id into its queries.
//  *
//  * Deliberately an explicit list rather than something derived from NAV_ITEMS'
//  * `scope` field: almost all of these are declared `scope: "org"` in nav.ts
//  * (only FIM is `scope: "agent"`), so the nav model can't tell them apart.
//  *
//  * NOT included, on purpose — these are org-scoped and must still render with
//  * no agent picked:
//  *   /endpoint-inventory   the list you pick an agent FROM
//  *   /org-dashboard, /organizations, /group-dashboard, /agent-enrollment,
//  *   /user-management, /fim-management, /alert-thresholds, /event-volume
//  *
//  * Add a route here when you add a new page that requires an agent.
//  */
// const AGENT_SCOPED_ROUTES: ReadonlySet<string> = new Set([
//   "/agent-dashboard",
//   "/system-monitoring",
//   "/cis-benchmark",
//   "/compliance",
//   "/vulnerabilities",
//   "/authentication-access",
//   "/threat-hunting",
//   "/file-integrity-monitoring",
// ]);

// export const AgentScopeGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { pathname } = useLocation();
//   const navigate = useNavigate();
//   const { selected } = useSelectedClient();
//   const orgId = useEffectiveOrgId();
//   const { can } = useCan();

//   // Same cache entry ClientSelector and AutoSelectClient already use, so this
//   // costs no extra network request — it just lets us tell "this org has no
//   // agents at all" apart from "agents exist, none selected yet".
//   const list = usePickerAgents(orgId);

//   const needsAgent = AGENT_SCOPED_ROUTES.has(pathname);
//   const hasAgent = Boolean(selected?.agent_id);

//   if (!needsAgent || hasAgent) return <>{children}</>;

//   // Still resolving which agent to use. Without this branch, switching org
//   // briefly paints the empty state before AutoSelectClient picks the new
//   // org's first agent — a visible flicker on every switch.
//   if (!orgId || list.isLoading || (list.isFetching && !list.data)) {
//     return (
//       <div className={styles.loading}>
//         <Spinner size={18} />
//         <span>Loading agents…</span>
//       </div>
//     );
//   }

//   const orgHasNoAgents = (list.data?.length ?? 0) === 0;

//   return (
//     <div className={styles.wrap}>
//       <div className={styles.card}>
//         <span className={`${styles.badge} ${orgHasNoAgents ? styles.badgeMuted : ""}`}>
//           <i className={`fas ${orgHasNoAgents ? "fa-server" : "fa-hand-pointer"}`} />
//         </span>

//         {orgHasNoAgents ? (
//           <>
//             <h2 className={styles.title}>No agents enrolled</h2>
//             <p className={styles.message}>
//               This organization doesn&apos;t have any agents yet. Enroll an agent to start collecting security data for this page.
//             </p>
//             {can("agent:enroll") && (
//               <div className={styles.actions}>
//                 <Button
//                   variant="primary"
//                   size="md"
//                   icon="fa-download"
//                   onClick={() => navigate("/agent-enrollment")}
//                 >
//                   Enroll an agent
//                 </Button>
//               </div>
//             )}
//           </>
//         ) : (
//           <>
//             <h2 className={styles.title}>Select an agent to continue</h2>
//             <p className={styles.message}>
//               This page reports on a single host. Choose which agent you&apos;d like
//               to view.
//             </p>
//             <span className={styles.hint}>
//               <i className="fas fa-arrow-up" />
//               Use the agent picker in the bar above
//             </span>
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// // /**
// //  * AgentScopeGate — stops agent-scoped pages from rendering when no client
// //  * is selected, showing a clear "pick a client" state instead.
// //  *
// //  * WHY THIS EXISTS
// //  * ---------------
// //  * Switching to an organization that has NO agents correctly clears the
// //  * selected client (AutoSelectClient calls select(null)). But the page the
// //  * user is standing on would keep showing the PREVIOUS agent's data, for
// //  * two reasons:
// //  *
// //  *   1. Agent pages key their queries on the agent id and gate them with
// //  *      `enabled: agentId !== ""`. With no agent the queries simply don't
// //  *      run — so nothing overwrites what's already on screen.
// //  *   2. Several of those queries use `placeholderData: (prev) => prev`,
// //  *      which deliberately keeps the last successful data visible while a
// //  *      new fetch is in flight. With no new fetch coming, that placeholder
// //  *      never goes away.
// //  *
// //  * Neither is a bug on its own — together they mean "no agent" looked
// //  * identical to "same agent as before". Rather than patching eight pages
// //  * (and every page added later), this gate makes the rule structural: if a
// //  * route needs an agent and there isn't one, the page doesn't render at all.
// //  *
// //  * Mounted once in AppShell, wrapping the page content.
// //  */
// // import { useLocation } from "react-router-dom";
// // import { EmptyState } from "@shared/ui";
// // import { useSelectedClient } from "./hooks/useRegistry";

// // /**
// //  * Routes whose content is meaningless without a selected agent — every page
// //  * that reads `useSelectedClient()` and feeds the agent id into its queries.
// //  *
// //  * Deliberately an explicit list rather than something derived from NAV_ITEMS'
// //  * `scope` field: almost all of these are declared `scope: "org"` in nav.ts
// //  * (only FIM is `scope: "agent"`), so the nav model can't tell them apart.
// //  *
// //  * NOT included, on purpose — these are org-scoped and must still render with
// //  * no agent picked:
// //  *   /endpoint-inventory   the list you pick an agent FROM
// //  *   /org-dashboard, /organizations, /group-dashboard, /agent-enrollment,
// //  *   /user-management, /fim-management, /alert-thresholds, /event-volume
// //  *
// //  * Add a route here when you add a new page that requires an agent.
// //  */
// // const AGENT_SCOPED_ROUTES: ReadonlySet<string> = new Set([
// //   "/agent-dashboard",
// //   "/system-monitoring",
// //   "/cis-benchmark",
// //   "/compliance",
// //   "/vulnerabilities",
// //   "/authentication-access",
// //   "/threat-hunting",
// //   "/file-integrity-monitoring",
// // ]);

// // export const AgentScopeGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
// //   const { pathname } = useLocation();
// //   const { selected } = useSelectedClient();

// //   const needsAgent = AGENT_SCOPED_ROUTES.has(pathname);
// //   const hasAgent = Boolean(selected?.agent_id);

// //   if (needsAgent && !hasAgent) {
// //     return (
// //       <EmptyState
// //         icon="fa-server"
// //         title="No client selected"
// //         message="This organization has no enrolled clients yet, or no client is selected. Pick one from the top navigation bar to see its data."
// //       />
// //     );
// //   }

// //   return <>{children}</>;
// // };